import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Upload, Save, UserPlus, Trash2, Building2, Banknote, Ban } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const limits = [
  { v: 1, l: "مرة واحدة" },
  { v: 2, l: "مرتان" },
  { v: 3, l: "ثلاث مرات" },
  { v: 0, l: "بلا حد" },
];

export default function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    association_name: "", logo_base64: null, charity_occurrence_limit: 1,
    bank_name: "", bank_iban: "", bank_account: "", bank_beneficiary: "", support_phone: "",
  });
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "staff" });
  const [openUser, setOpenUser] = useState(false);

  const load = async () => {
    try {
      const [s, u] = await Promise.all([
        api.get("/settings"),
        user?.role === "admin" ? api.get("/users") : Promise.resolve({ data: [] }),
      ]);
      setSettings(s.data);
      setUsers(u.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onLogo = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("الحد الأقصى للصورة 500KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSettings({ ...settings, logo_base64: reader.result });
    reader.readAsDataURL(file);
  };

  const saveSettings = async () => {
    try {
      await api.put("/settings", settings);
      toast.success("تم حفظ الإعدادات");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", newUser);
      toast.success("تم إضافة المستخدم");
      setNewUser({ email: "", password: "", name: "", role: "staff" });
      setOpenUser(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeUser = async (id) => {
    if (!window.confirm("هل تريد حذف هذا المستخدم؟")) return;
    try { await api.delete(`/users/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-5xl mx-auto p-6 lg:p-10">
        <div className="mb-4">
          <Button variant="ghost" onClick={() => navigate("/")}
            data-testid="settings-back" className="text-emerald-900 hover:bg-emerald-50">
            <ArrowRight className="h-4 w-4 ml-2" /> العودة
          </Button>
        </div>
        <PageHeader title="الإعدادات" subtitle="بيانات الجمعية، المستخدمون، وإعدادات التكرار"
          actions={
            <Button data-testid="settings-blacklist-btn" variant="outline" onClick={() => navigate("/blacklist")}
              className="border-red-600 text-red-700 hover:bg-red-50">
              <Ban className="h-4 w-4 ml-2" /> القائمة السوداء
            </Button>
          }
        />

        <Card className="p-6 mb-6 border-emerald-900/10">
          <div className="flex items-center gap-2 mb-6">
            <Building2 className="h-5 w-5 text-emerald-800" />
            <h3 className="font-heading font-bold text-xl text-emerald-950">بيانات الجمعية</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label>اسم الجمعية</Label>
              <Input data-testid="settings-name" value={settings.association_name}
                onChange={(e) => setSettings({ ...settings, association_name: e.target.value })} />
            </div>
            <div>
              <Label>حد ظهور المستفيد في البوابة الخيرية</Label>
              <Select value={String(settings.charity_occurrence_limit)}
                onValueChange={(v) => setSettings({ ...settings, charity_occurrence_limit: Number(v) })}>
                <SelectTrigger data-testid="settings-limit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {limits.map((l) => (<SelectItem key={l.v} value={String(l.v)}>{l.l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>شعار الجمعية (حد أقصى 500KB)</Label>
              <div className="flex items-center gap-4 mt-2">
                {settings.logo_base64 && (
                  <img src={settings.logo_base64} alt="logo" className="h-20 w-20 rounded-xl object-cover border border-emerald-900/10" />
                )}
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-900/20 text-emerald-900 hover:bg-emerald-50">
                  <Upload className="h-4 w-4" /> رفع صورة
                  <input type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="settings-logo-upload" />
                </label>
                {settings.logo_base64 && (
                  <Button variant="ghost" onClick={() => setSettings({ ...settings, logo_base64: null })}
                    className="text-red-600 hover:bg-red-50">حذف</Button>
                )}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6 mb-6 border-emerald-900/10">
          <div className="flex items-center gap-2 mb-6">
            <Banknote className="h-5 w-5 text-emerald-800" />
            <h3 className="font-heading font-bold text-xl text-emerald-950">بيانات التحويل البنكي</h3>
          </div>
          <p className="text-sm text-slate-500 mb-4">تظهر للمستفيدين في صفحة التسجيل الذاتي عبر الرابط.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>اسم البنك</Label>
              <Input data-testid="settings-bank-name" value={settings.bank_name || ""}
                onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })} />
            </div>
            <div><Label>اسم المستفيد</Label>
              <Input data-testid="settings-bank-beneficiary" value={settings.bank_beneficiary || ""}
                onChange={(e) => setSettings({ ...settings, bank_beneficiary: e.target.value })} />
            </div>
            <div><Label>رقم الآيبان (IBAN)</Label>
              <Input data-testid="settings-bank-iban" dir="ltr" value={settings.bank_iban || ""}
                onChange={(e) => setSettings({ ...settings, bank_iban: e.target.value })} />
            </div>
            <div><Label>رقم الحساب</Label>
              <Input data-testid="settings-bank-account" dir="ltr" value={settings.bank_account || ""}
                onChange={(e) => setSettings({ ...settings, bank_account: e.target.value })} />
            </div>
            <div className="md:col-span-2"><Label>رقم خدمة العملاء</Label>
              <Input data-testid="settings-support-phone" dir="ltr" value={settings.support_phone || ""}
                onChange={(e) => setSettings({ ...settings, support_phone: e.target.value })} />
            </div>
          </div>
        </Card>

        <div className="mb-6">
          <Button data-testid="settings-save" onClick={saveSettings}
            className="bg-emerald-900 hover:bg-emerald-800 text-white">
            <Save className="h-4 w-4 ml-2" /> حفظ الإعدادات
          </Button>
        </div>

        {user?.role === "admin" && (
          <Card className="p-6 border-emerald-900/10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-emerald-800" />
                <h3 className="font-heading font-bold text-xl text-emerald-950">إدارة المستخدمين</h3>
              </div>
              <Dialog open={openUser} onOpenChange={setOpenUser}>
                <DialogTrigger asChild>
                  <Button data-testid="user-add-btn" className="bg-emerald-900 hover:bg-emerald-800 text-white">
                    <UserPlus className="h-4 w-4 ml-2" /> مستخدم جديد
                  </Button>
                </DialogTrigger>
                <DialogContent dir="rtl">
                  <DialogHeader><DialogTitle className="text-right">إضافة مستخدم</DialogTitle></DialogHeader>
                  <form onSubmit={createUser} className="space-y-4">
                    <div><Label>الاسم</Label>
                      <Input data-testid="user-form-name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} required />
                    </div>
                    <div><Label>البريد الإلكتروني</Label>
                      <Input type="email" data-testid="user-form-email" value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required />
                    </div>
                    <div><Label>كلمة المرور</Label>
                      <Input type="password" data-testid="user-form-password" value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} minLength={6} required />
                    </div>
                    <div><Label>الدور</Label>
                      <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                        <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="staff">موظف</SelectItem>
                          <SelectItem value="admin">مدير</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <DialogFooter>
                      <Button type="submit" data-testid="user-form-save"
                        className="bg-emerald-900 hover:bg-emerald-800 text-white">إضافة</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-right" data-testid="users-table">
                <thead className="bg-emerald-950 text-white">
                  <tr>
                    <th className="p-3 font-heading">الاسم</th>
                    <th className="p-3 font-heading">البريد</th>
                    <th className="p-3 font-heading">الدور</th>
                    <th className="p-3 font-heading w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <tr key={u.id} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                      <td className="p-3 font-medium text-emerald-950">{u.name}</td>
                      <td className="p-3 text-slate-700 dir-ltr" style={{ direction: "ltr", textAlign: "right" }}>{u.email}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${u.role === "admin" ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-700"}`}>
                          {u.role === "admin" ? "مدير" : "موظف"}
                        </span>
                      </td>
                      <td className="p-3">
                        {u.id !== user.id && (
                          <Button size="sm" variant="ghost" data-testid={`user-delete-${u.id}`}
                            onClick={() => removeUser(u.id)} className="text-red-600 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
